import { debounce, fetchConfig } from '@theme/utilities';

class CartPoNumber extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Promise<void> | null} */
  #pendingUpdate = null;

  #hasUnsavedChange = false;

  #latestPoNumber = '';

  connectedCallback() {
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('click', this.#handleCheckoutClick);
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('click', this.#handleCheckoutClick);
  }

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'attributes[PO Number]') return;

    this.#syncFields(event.target.value);
    this.#latestPoNumber = event.target.value;
    this.#hasUnsavedChange = true;
    this.#queuePoNumberUpdate();
  };

  #handleCheckoutClick = async (event) => {
    const checkoutLink = event.target instanceof Element ? event.target.closest('a[href*="/checkout"]') : null;
    if (!checkoutLink || (!this.#pendingUpdate && !this.#hasUnsavedChange)) return;

    event.preventDefault();

    if (this.#pendingUpdate) {
      await this.#pendingUpdate;
    }

    if (this.#hasUnsavedChange) {
      await this.#saveLatestPoNumber();
    }

    if (checkoutLink instanceof HTMLAnchorElement) {
      window.location.href = checkoutLink.href;
    }
  };

  #queuePoNumberUpdate = debounce(() => {
    this.#saveLatestPoNumber();
  }, 200);

  #saveLatestPoNumber() {
    if (!this.#hasUnsavedChange) return Promise.resolve();

    const poNumber = this.#latestPoNumber;
    this.#hasUnsavedChange = false;
    this.#pendingUpdate = this.#savePoNumber(poNumber).finally(() => {
      this.#pendingUpdate = null;
    });

    return this.#pendingUpdate;
  }

  async #savePoNumber(poNumber) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            'PO Number': poNumber,
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  #syncFields(value) {
    document.querySelectorAll('input[name="attributes[PO Number]"]').forEach((input) => {
      if (input instanceof HTMLInputElement && input.value !== value) {
        input.value = value;
      }
    });
  }
}

if (!customElements.get('cart-po-number')) {
  customElements.define('cart-po-number', CartPoNumber);
}
